/*
 * Linear paint kit sizing — single source of truth.
 * ---------------------------------------------------------------------------
 * Both the paint kit builder (paint-kit-builder.js) and the OEMatch bridge
 * (oematch-search-bridge.js) read their quantities from this file. Editing the
 * tables below changes both paths at once. Do not copy these rules into either
 * consumer; they diverged once already and the OEMatch path silently
 * under-sized every kit.
 *
 * Sizing intent, owner-approved 2026-08-07: finish a job with paint left over
 * rather than run short mid-panel. Left-over paint also covers future repairs.
 *
 * The reduction math that drives the two columns:
 *   Non-reduced basecoat is concentrate. It reduces 1:1, so one gallon of
 *   concentrate sprays two gallons.
 *   Ready to spray arrives already reduced 50/50, so one gallon sprays one
 *   gallon and the same job needs roughly double the units.
 */
(function () {
  const SIZING = {
    /*
     * Basecoat packages per vehicle size and coverage.
     * gallons/quarts are the number of packages to put in the cart.
     * To change how much paint a job gets, edit these numbers.
     */
    basecoat: {
      'Small car / compact': {
        'Exterior only':      { nonReduced: { gallons: 1, quarts: 0 }, readyToSpray: { gallons: 1, quarts: 2 } },
        'Full color change':  { nonReduced: { gallons: 1, quarts: 1 }, readyToSpray: { gallons: 2, quarts: 2 } },
      },
      'Midsize car': {
        'Exterior only':      { nonReduced: { gallons: 1, quarts: 0 }, readyToSpray: { gallons: 2, quarts: 0 } },
        'Full color change':  { nonReduced: { gallons: 1, quarts: 2 }, readyToSpray: { gallons: 3, quarts: 0 } },
      },
      'Large car / small SUV': {
        'Exterior only':      { nonReduced: { gallons: 1, quarts: 1 }, readyToSpray: { gallons: 2, quarts: 2 } },
        'Full color change':  { nonReduced: { gallons: 2, quarts: 0 }, readyToSpray: { gallons: 4, quarts: 0 } },
      },
      'Full-size truck / SUV': {
        'Exterior only':      { nonReduced: { gallons: 1, quarts: 2 }, readyToSpray: { gallons: 3, quarts: 0 } },
        'Full color change':  { nonReduced: { gallons: 2, quarts: 1 }, readyToSpray: { gallons: 4, quarts: 2 } },
      },
    },

    /* Used when a vehicle size or coverage is missing or unrecognised. */
    basecoatFallback: { nonReduced: { gallons: 1, quarts: 0 }, readyToSpray: { gallons: 2, quarts: 0 } },

    /*
     * Reducer (AR-3000). Sized from Alpha's published mix ratios rather than a
     * rule of thumb, so it is arithmetic rather than an estimate.
     *
     * Two things consume it, and only two:
     *   1. Non-reduced basecoat, which reduces 1:1 — a litre of concentrate
     *      takes a litre of reducer.
     *   2. The AP-7541 sealer. Per its TDS the sealer ratio is 4:1:2, i.e.
     *      100% AP-7541 : 25% AH-7100 activator : 50% reducer. The 4.5 L kit
     *      holds 3.5 L of AP-7541, so it needs 1.75 L of reducer to spray.
     *
     * The AC-4521 clearcoat needs NONE. Its TDS mixes 2:1 with HS Activator
     * and states "no reducer required"; the activator ships inside the kit.
     * Do not add a reducer allowance for clear.
     */
    reducer: {
      litresPerPackage: { large: 5, small: 1 },
      sealerLitresByKit: { '4.5 Liter': 1.75, '1.25 Liter': 0.5 },
      defaultSealerLitres: 1.75,
      /*
       * Mixing is not the only thing reducer gets used for - guns, cups and
       * lines all get flushed with it, and none of that appears in a mix
       * ratio. Owner figure: allow 25-30% over the mixed volume. Sized at the
       * top of that range so a job does not stall on solvent.
       */
      cleanupAllowance: 1.3,
    },

    /* Alpha sells basecoat by US gallon and quart. */
    litres: { gallon: 3.785, quart: 0.946 },

    /*
     * Clearcoat AC-4521, sized from painted area rather than a flat package
     * count. Coverage is 6-7 m2/L per layer at 50um; 6.5 is the midpoint. At
     * 75% practical transfer that is about 4.9 m2/L on the car.
     *
     * A kit's volume is already its sprayable volume: 7.5 L is 5 L of clear
     * plus 2.5 L of activator at 2:1, and no reducer goes in.
     *
     * Alpha specs two full coats. A show finish stacks film so there is
     * something to cut and buff, which is why it carries its own coat count
     * rather than a multiplier on the driver figure.
     */
    clearcoat: {
      coverageM2PerLitre: 6.5,
      transferEfficiency: 0.75,
      safetyMargin: 1.15,
      litresPerPackage: { large: 7.5, small: 1.5 },
      coats: { 'Driver finish': 2, 'Show finish': 5 },
      coatsFallback: 2,
    },

    /*
     * Activator and reducer speed by booth temperature.
     *
     * Reducer follows the AP-7541 TDS exactly: AR-3070 medium covers 55-85F,
     * AR-3080 slow above 85F.
     *
     * Clearcoat runs deliberately one step slower than the TDS minimum. Alpha
     * allows medium up to 85F and medium-or-slow from 85-95F, but a slower
     * activator buys flow-out time and reduces the risk of solvent pop, which
     * matters more as film build goes up. Owner direction 2026-08-07: err slow
     * on clear.
     */
    speed: {
      reducer: { '65–75°F': 'Medium', '76–85°F': 'Medium', 'Above 85°F': 'Slow' },
      clearcoat: { '65–75°F': 'Medium', '76–85°F': 'Slow', 'Above 85°F': 'Slow' },
      fallback: 'Medium',
    },

    /* Conditions that require the Linear epoxy primer in the kit. */
    conditionsNeedingBodywork: ['Light bodywork', 'Ground-up restoration'],

    /*
     * Body area in square metres, used to size anything sprayed over the whole
     * car. Exterior-only figures; full colour change adds jambs, under-hood and
     * under-trunk via the multiplier below.
     */
    bodyArea: {
      'Small car / compact': 8,
      'Midsize car': 11,
      'Large car / small SUV': 15,
      'Full-size truck / SUV': 18,
    },
    bodyAreaFallback: 11,
    coverageMultiplier: { 'Exterior only': 1, 'Full color change': 1.4 },

    /*
     * Alpha sealer AP-7541. Linear sprays it as ONE reduced coat immediately
     * before colour, so it is sized off a single layer rather than the TDS
     * range of 1-2 coats.
     *
     * Coverage is 8 m2/L per layer at 60um. At 75% practical transfer that is
     * 6 m2/L on the car. A kit's sprayable volume is its AP-7541 content times
     * 1.75, since 4:1:2 adds 25% activator and 50% reducer by volume.
     */
    sealer: {
      coverageM2PerLitre: 8,
      transferEfficiency: 0.75,
      safetyMargin: 1.3,
      rtsMultiplier: 1.75,
      kits: { '1.25 Liter': 1, '4.5 Liter': 3.5 },
      colors: ['Black', 'Gray', 'White'],
    },

    /*
     * Linear high-build epoxy primer, sold as a gallon kit. Owner figure:
     * a ground-up midsize takes about two gallons. Light bodywork is spot
     * work, so it does not scale with the car.
     */
    epoxyPrimerGallons: {
      'Light bodywork': {
        'Small car / compact': 1, 'Midsize car': 1,
        'Large car / small SUV': 1, 'Full-size truck / SUV': 1,
      },
      'Ground-up restoration': {
        'Small car / compact': 2, 'Midsize car': 2,
        'Large car / small SUV': 3, 'Full-size truck / SUV': 4,
      },
    },
  };

  const QUARTS_PER_GALLON = 4;

  function needsBodywork(condition) {
    return SIZING.conditionsNeedingBodywork.indexOf(condition) !== -1;
  }

  /*
   * project: { basecoatPreparation, size, coverage, clearcoatBuild, condition }
   * returns { gallons, quarts, reducerPackages, clearcoatPackages, needsBodywork }
   */
  function plan(project) {
    project = project || {};
    const isNonReduced = project.basecoatPreparation === 'Non-reduced';
    const bySize = SIZING.basecoat[project.size];
    const row = (bySize && bySize[project.coverage]) || SIZING.basecoatFallback;
    const picked = isNonReduced ? row.nonReduced : row.readyToSpray;
    const gallons = picked.gallons;
    const quarts = picked.quarts;

    // Painted area drives anything sprayed over the whole car.
    const area = (SIZING.bodyArea[project.size] || SIZING.bodyAreaFallback)
      * (SIZING.coverageMultiplier[project.coverage] || 1);

    // Sealer: one reduced coat before colour.
    const s = SIZING.sealer;
    const sealerRtsNeeded = (area / (s.coverageM2PerLitre * s.transferEfficiency)) * s.safetyMargin;
    const smallKitRts = s.kits['1.25 Liter'] * s.rtsMultiplier;
    const largeKitRts = s.kits['4.5 Liter'] * s.rtsMultiplier;
    let sealerLarge = 0;
    let sealerSmall = Math.max(1, Math.ceil(sealerRtsNeeded / smallKitRts));
    // Once enough small kits are stacking up, one large kit is the tidier buy.
    if (sealerSmall * smallKitRts >= largeKitRts) {
      sealerLarge = 1;
      sealerSmall = Math.max(0, Math.ceil((sealerRtsNeeded - largeKitRts) / smallKitRts));
    }

    // Epoxy primer, in gallon kits.
    const primerBySize = SIZING.epoxyPrimerGallons[project.condition];
    const epoxyPrimerGallons = primerBySize ? (primerBySize[project.size] || 1) : 0;

    // Litres of reducer this job actually consumes: 1:1 for non-reduced
    // basecoat, plus 50% of the AP-7541 volume going into sealer.
    const basecoatLitres = gallons * SIZING.litres.gallon + quarts * SIZING.litres.quart;
    const sealerBaseLitres = sealerLarge * s.kits['4.5 Liter'] + sealerSmall * s.kits['1.25 Liter'];
    const sealerLitres = sealerBaseLitres * 0.5;
    const reducerLitres = ((isNonReduced ? basecoatLitres : 0) + sealerLitres)
      * SIZING.reducer.cleanupAllowance;

    // Cover it with as few packages as possible, always rounding up so the shop
    // finishes with a little spare rather than running dry mid-panel.
    const big = SIZING.reducer.litresPerPackage.large;
    let reducerLarge = Math.floor(reducerLitres / big);
    let reducerSmall = Math.ceil(reducerLitres - reducerLarge * big);
    if (reducerSmall >= big) { reducerLarge += 1; reducerSmall = 0; }

    // Clearcoat, from painted area and how many coats they are laying down.
    const cc = SIZING.clearcoat;
    const coats = cc.coats[project.clearcoatBuild] || cc.coatsFallback;
    const clearcoatLitres = (area * coats)
      / (cc.coverageM2PerLitre * cc.transferEfficiency)
      * cc.safetyMargin;
    let clearcoatLarge = Math.floor(clearcoatLitres / cc.litresPerPackage.large);
    let clearcoatSmall = Math.ceil(
      (clearcoatLitres - clearcoatLarge * cc.litresPerPackage.large) / cc.litresPerPackage.small
    );
    if (clearcoatSmall * cc.litresPerPackage.small >= cc.litresPerPackage.large) {
      clearcoatLarge += 1;
      clearcoatSmall = 0;
    }
    const clearcoatPackages = Math.max(1, clearcoatLarge || clearcoatSmall);

    return {
      gallons: gallons,
      quarts: quarts,
      reducerLitres: Math.round(reducerLitres * 100) / 100,
      reducerLarge: reducerLarge,
      reducerSmall: reducerSmall,
      reducerPackages: Math.max(1, reducerLarge || reducerSmall),
      clearcoatPackages: clearcoatPackages,
      clearcoatLitres: Math.round(clearcoatLitres * 100) / 100,
      clearcoatLarge: clearcoatLarge,
      clearcoatSmall: clearcoatSmall,
      clearcoatCoats: coats,
      sealerLarge: sealerLarge,
      sealerSmall: sealerSmall,
      epoxyPrimerGallons: epoxyPrimerGallons,
      needsBodywork: needsBodywork(project.condition),
    };
  }

  /* role: 'reducer' | 'clearcoat'. Returns 'Medium' or 'Slow'. */
  function speedFor(role, temperature) {
    const table = SIZING.speed[role] || SIZING.speed.reducer;
    return table[temperature] || SIZING.speed.fallback;
  }

  window.LinearKitSizing = { config: SIZING, plan: plan, needsBodywork: needsBodywork, speedFor: speedFor };
})();
